import { useState } from 'react';
import { ChevronsUpDown, ChevronsDownUp } from 'lucide-react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import TextWithClickableQuotes from '@/utils/TextWithClickableQuotes';
import CopyButton from '@/components/ui/CopyButton';
import { formatSectionForClipboard } from '@/utils/formatForClipboard';

interface QuestionData {
  title: string;
  body: string;
  index: number;
}

interface QuestionAnswerAccordionProps {
  data: QuestionData[];
  showExpandAll?: boolean;
  voiceQuotes?: Array<{ conversation_id: string; message_id?: string }>;
}

function QuestionAnswerAccordion({ data, showExpandAll = false, voiceQuotes }: QuestionAnswerAccordionProps) {
  if (!data?.length) return null;

  const allValues = data.map((item) => `question-${item.index}`);
  // Auto-expand first 5 questions so researchers see content immediately
  const initialOpen = data.slice(0, 5).map((item) => `question-${item.index}`);

  const [expandedItems, setExpandedItems] = useState<string[]>(initialOpen);

  const allExpanded = expandedItems.length === allValues.length;

  const handleToggleAll = () => {
    setExpandedItems(allExpanded ? [] : allValues);
  };

  return (
    <div className="flex flex-col gap-3">
      {showExpandAll && (
        <div className="flex justify-end">
          <button
            type="button"
            onClick={handleToggleAll}
            className="flex items-center gap-1 text-xs text-neutral-500 hover:text-neutral-700 transition-colors"
          >
            {allExpanded ? (
              <>
                <ChevronsDownUp className="h-3.5 w-3.5" />
                Collapse all
              </>
            ) : (
              <>
                <ChevronsUpDown className="h-3.5 w-3.5" />
                Expand all
              </>
            )}
          </button>
        </div>
      )}
      <Accordion
        type="multiple"
        value={expandedItems}
        onValueChange={setExpandedItems}
        className="flex flex-col gap-3"
      >
        {data.map((item) => (
          <AccordionItem
            key={item.index}
            value={`question-${item.index}`}
            className="rounded-lg border border-neutral-200/80 bg-white shadow-sm overflow-hidden border-b-[1px]"
          >
            <div className="flex items-center px-4">
              <AccordionTrigger className="hover:no-underline flex-1 py-3.5 gap-3">
                <div className="flex items-center gap-3 text-left">
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-xs font-semibold text-emerald-700">
                    {item.index + 1}
                  </span>
                  <span className="text-sm font-semibold text-neutral-800">{item.title}</span>
                </div>
              </AccordionTrigger>
              <CopyButton
                text={formatSectionForClipboard(item.title, item.body)}
                className="shrink-0"
              />
            </div>
            <AccordionContent className="px-4 pb-4 pt-0">
              <div className="pl-9 max-w-prose text-[14px] leading-relaxed text-neutral-600">
                <TextWithClickableQuotes text={item.body} voiceQuotes={voiceQuotes} />
              </div>
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  );
}

export default QuestionAnswerAccordion;
